<template>
  <div>
    <!-- Subórdenes -->
    <v-card
      v-for="item in suborders"
      :key="`suborders-${order}-${item.pk}`"
      class="suborder--card"
      @click.stop="handleClick(item)"
    >
      <div
        v-if="subOrder !== undefined ? item.pk === subOrder : true"
        class="suborder--container"
      >
        <!-- Imagen y código de la suborden -->
        <div class="suborder--image">
          <i-image
            class="product-image mb-2"
            icon="false"
            :style="{ width: '5em !important', height: '4em !important' }"
            :value="item.thumbnail"
            :preview="true"
            :readonly="true"
          />
          <p v-if="!calendar" class="suborder--caption">
            {{ item.code }}
          </p>
        </div>

        <!-- Nombre de la suborden, nombre de la orden y del producto -->
        <div v-if="!calendar" class="suborder--text">
          <p class="suborder--title">
            {{ item.name }}
          </p>
          <p v-if="showSubOrderTitle" class="suborder--subtitle">
            {{ $tc('order', 1) }}: {{ item.projectName }}
          </p>
          <p class="suborder--caption">
            {{ item.own_prod }}
          </p>
          <p
            v-if="
              inProduction(item.orderStatus) &&
              checkPermissions.isElevatedRole &&
              checkPermissions.isProducer
            "
          >
            <v-btn
              color="primary"
              outlined
              x-small
              class="text-capitalize mt-2"
              @click.stop="$refs.producerModal.open(item.pk)"
            >
              <v-icon x-small>mdi mdi-eye</v-icon> {{ $tc('as_producer') }}
            </v-btn>
          </p>
        </div>

        <!-- Estados de la suborden -->
        <div class="suborder--percentajes">
          <div
            v-for="(progress, key) in status[item.pk]"
            :key="`progress-${item.pk}-${key}`"
            class="minisuborder-progress-container"
          >
            <v-progress-circular
              :color="setColor(progress.color)"
              :rotate="-90"
              :size="50"
              :value="progress.value"
              :width="5"
            >
              {{ `${progress.value}%` }}
            </v-progress-circular>
            <div
              class="minisuborder__status_name"
              :style="{ color: setColor(progress.color) }"
            >
              {{ $tc(progress.name, 1) }}
            </div>
          </div>
        </div>

        <!-- Suborden en delivery -->
        <div class="suborder--delivery">
          <p class="suborder--title mb-0">
            {{ $t(item.leader_status['delivery']) }}
          </p>
        </div>
      </div>
    </v-card>

    <!-- Modal normal de suborden -->
    <dial-prod
      v-if="checkPermissions.isElevatedRole || !checkPermissions.isProducer"
      ref="dialprod"
      @prod="getSuborder($event)"
      @deleted="getSuborder()"
    />

    <!-- Modal de productores -->
    <producer-modal
      v-if="checkPermissions.isElevatedRole || checkPermissions.isProducer"
      ref="producerModal"
      @prod="getSuborder()"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DialProd from './DialogProduct.vue'
import ProducerModal from './ProducerModal.vue'
import '@/pages/sale/sale.sass'

export default {
  components: {
    DialProd,
    ProducerModal
  },
  props: {
    order: Number,
    subOrder: Number,
    calendar: {
      type: Boolean,
      default: false
    },
    miniSubOrder: Object,
    openModal: {
      type: Boolean,
      default: false // Prop para recibir la señal desde el padre
    },
    showSubOrderTitle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      suborders: [],
      status: {},
      loading: false,
      postStatus: {}
    }
  },
  watch: {
    order: {
      immediate: true,
      handler(val) {
        if (val !== undefined) {
          this.getSuborder()
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin'
    }),
    /**
     * Determina si el usuario tiene los permisos necesarios según la indicación
     *
     * @return {boolean}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkPermissions() {
      return {
        isElevatedRole:
          this.getPermissions([`users.change_profile`]) &&
          this.getPermissions([`auth.change_user`]),
        isDesigner:
          this.getPermissions([`sales.change_assigneduser`]) &&
          this.getPermissions([`sales.view_assigneduser`]),
        isProducer:
          this.getPermissions([`sales.view_assigneddepartment`]) &&
          this.getPermissions([`sales.change_assigneddepartment`])
      }
    }
  },
  methods: {
    /**
     * Método que maneja el clic en una suborden en el listado de órdenes.
     * Verifica si la cantidad del ítem es mayor que cero y si el usuario tiene
     * permisos para ver el producto de la orden o es admin.
     * Si se cumplen las condiciones, abre la modal del producto.
     *
     * @param item - El ítem sobre el cual se ha hecho clic.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    handleClick(item) {
      if (
        this.checkPermissions.isProducer &&
        !this.checkPermissions.isElevatedRole
      ) {
        this.$refs.producerModal.open(item.pk)
      } else {
        if (
          item.quantity > 0 &&
          this.getPermissions(['sales.view_orderproduct'])
        ) {
          this.$refs.dialprod.open(item.pk)
        }
      }
    },
    /**
     * Abrir el modal de la sub-orden cuando el click viene de una notificación
     * del chat.
     *
     * @param id - id de la sub-orden.
     *
     * @author Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    openModalFunction(id) {
      // Enviar la señal de abril del modal de la sub-orden.
      this.$refs.dialprod.open(id)
      // Enviar la orden abrir la pestaña chad del modal.
      this.$refs.dialprod.clickChatButton(4)
    },
    /**
     * Método asíncrono para obtener las subordenes de una orden específica.
     * Realiza una llamada a la API para recuperar los datos de subordenes,
     * establece el estado de carga y maneja errores durante la recuperación.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getSuborder() {
      this.loading = true
      try {
        const response = await this.$api.sale.suborder.show({
          pk: this.order,
          opt: { params: { order: true } }
        })

        this.suborders = response.data
        await Promise.all(this.suborders.map(this.fetchStatusForSuborder))
      } catch (error) {
        console.error('Error fetching suborders:', error)
      } finally {
        this.loading = false
      }
    },
    /**
     * Método asíncrono que recupera el estado de una suborden específica.
     * Si el templateId es válido y no se ha recuperado previamente, realiza una
     * llamada a la API para obtener los estados.
     * Luego, calcula y emite el estado de la suborden.
     *
     * @param suborder - La suborden para la cual se recupera el estado.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async fetchStatusForSuborder(suborder) {
      const templateId = suborder.template

      if (templateId > 0 && !this.postStatus[templateId]) {
        const stagesResponse = await this.$api.sale.status.list({
          opt: { params: { template: templateId } }
        })
        this.postStatus[templateId] = stagesResponse.data
      }

      const stages = this.postStatus[templateId] || []
      let activeStage = stages.find((stage) =>
        stage.substatusVals.some((val) => val.pk == suborder.status)
      )

      if (!activeStage && suborder.orderStatus) {
        const matches = stages.filter((stage) => {
          const rawName = stage.name.toLowerCase()
          return suborder.orderStatus.toLowerCase().includes(rawName)
        })

        if (matches.length > 0) {
          activeStage = matches.sort((a, b) => b.name.length - a.name.length)[0]
        }
      }

      const activeStageIndex = stages.indexOf(activeStage)
      let rootId = 0

      if (activeStage) {
        const subStatus = activeStage.substatusVals.find(
          (item) => item.pk == suborder.status
        )
        if (subStatus) {
          rootId = subStatus.root
        } else {
          rootId = activeStage.pk
        }
      }

      const statuses = stages
        .filter(
          (stage) => stage.name !== 'preprocess' && stage.name !== 'delivery'
        )
        .map((stage, index) =>
          this.calculateStageStatus(
            stage,
            suborder,
            rootId,
            activeStage,
            stages.indexOf(stage),
            activeStageIndex
          )
        )

      this.$set(this.status, suborder.pk, statuses)
      this.$emit('update:miniSubOrder', suborder)
      this.$emit('done', suborder)
    },
    /**
     * Calcula el estado de una etapa específica para una suborden.
     * Establece el porcentaje basado en la posición de la suborden dentro de
     * la etapa.
     *
     * @param stage - La etapa para la cual se calcula el estado.
     * @param suborder - La suborden que se está evaluando.
     * @returns {Object} Un objeto con información sobre la etapa y su valor.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    calculateStageStatus(
      stage,
      suborder,
      rootId,
      activeStage,
      stageIndex,
      activeStageIndex
    ) {
      if (activeStageIndex !== -1 && stageIndex < activeStageIndex) {
        return {
          pk: stage.pk,
          name: stage.name,
          color: stage.color,
          value: 100
        }
      }

      if (activeStageIndex !== -1 && stageIndex > activeStageIndex) {
        return {
          pk: stage.pk,
          name: stage.name,
          color: stage.color,
          value: 0
        }
      }

      let subStatus = null

      if (
        activeStage &&
        !stage.substatusVals.some((item) => item.pk == suborder.status)
      ) {
        const statusLower = (suborder.orderStatus || '').toLowerCase()
        const matchingSubstatuses = stage.substatusVals.filter((sub) => {
          const rawName = (sub.name || '').toLowerCase()
          const translatedName = this.$tc(sub.name || '', 1).toLowerCase()

          return (
            statusLower.includes(rawName) ||
            statusLower.includes(translatedName)
          )
        })

        if (matchingSubstatuses.length > 0) {
          subStatus = matchingSubstatuses.sort(
            (a, b) => (b.name || '').length - (a.name || '').length
          )[0]
        }
      } else {
        subStatus = stage.substatusVals.find(
          (item) => item.pk == suborder.status
        )
      }

      let percent = 0

      if (subStatus) {
        const index = stage.substatusVals.indexOf(subStatus)
        const total = stage.substatusVals.length

        percent = Math.round((index / total) * 100)
      } else {
        const statusLower = (suborder.orderStatus || '').toLowerCase()

        if (statusLower.includes('lista') || statusLower.includes('ready')) {
          percent = 100
        } else if (
          statusLower.includes('en proceso') ||
          statusLower.includes('process')
        ) {
          percent = 50
        }
      }

      if (stage.pk > rootId) {
        percent = 0
      }

      return {
        pk: stage.pk,
        name: stage.name,
        color: stage.color,
        value: Math.max(0, Math.min(percent, 100))
      }
    },
    /**
     * Determina si una etapa está posicionada para una suborden dada.
     * Retorna verdadero si la suborden está en la etapa o si no tiene estado.
     *
     * @param stage - La etapa a evaluar.
     * @param suborder - La suborden que se está evaluando.
     * @returns {boolean} Verdadero si la suborden está en la etapa, falso en
     *                    caso contrario.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    calculateStatusValue(stage, suborder) {
      if (suborder.mainStatus === 'finished' || suborder.status === null) {
        return false
      }

      if (stage.pk == suborder.status || stage.substatusVals.length === 0) {
        return true
      }

      return stage.substatusVals.some(
        (substatus) => substatus.pk == suborder.status
      )
    },
    /**
     * Determina un color basado en el color proporcionado.
     * Si el color es blanco, devuelve negro.
     *
     * @param color - El color a evaluar.
     * @returns {string} El color adecuado para el progreso.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    setColor(color) {
      return color === '#FFFFFF' || color === '#FFF' ? 'black' : color
    },
    /**
     * Método que verifica si la órden de una suborden está en producción.
     *
     * @param {String} orderStatus - Estatus de la orden
     * @returns {Boolean}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    inProduction(orderStatus = '') {
      const status = orderStatus.indexOf('production')
      return status >= 0 ? true : false
    },
    openSuborderModal(suborderId) {
      if (
        this.checkPermissions.isProducer &&
        !this.checkPermissions.isElevatedRole
      ) {
        this.$refs.producerModal.open(suborderId)
      } else {
        this.$refs.dialprod.open(suborderId)
      }
    }
  },
  mounted() {
    // Señal para abrir el modal de la suborden desde una notificaióin del chat.
    if (this.openModal) {
      this.openModalFunction(this.subOrder)
    }
  }
}
</script>
